import React from "react";
import { ArrowUpRight, CheckCircle2, Users, RefreshCw, Lock, CalendarDays, AlertTriangle, Mail, Phone } from "lucide-react";
import { PageShell, SubpageHero } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import { useSeo } from "@/hooks/useSeo";
import { GuideDownloadButton } from "@/components/GuideDownloadButton";

const HOW_IT_WORKS = [
  { icon: Users, title: "Who it affects", items: ["Self-employed individuals", "Landlords", "Qualifying income over £50,000 (then £30,000, then £20,000)"] },
  { icon: RefreshCw, title: "What is changing", items: ["Keep digital records", "Use HMRC-compatible software", "Send quarterly updates to HMRC"] },
  { icon: Lock, title: "What stays the same", items: ["You still submit a final tax return", "Deadline remains 31 January", "Tax is still paid on 31 January & 31 July"] },
  { icon: CalendarDays, title: "What are quarterly updates", items: ["A summary of income & expenses", "Sent 4 times per year from your software", "Deadlines: 7 Aug, 7 Nov, 7 Feb, 7 May"] },
];

const START_DATES = [
  { income: "Over £50,000", year: "in the 2024/25 tax year", start: "6 April 2026", status: "Now live" },
  { income: "Over £30,000", year: "in the 2025/26 tax year", start: "6 April 2027", status: "Next" },
  { income: "Over £20,000", year: "qualifying income", start: "6 April 2028", status: "Later" },
];

const KEY_DATES = [
  { date: "31 January 2026", title: "Submit your 2024/25 Self Assessment tax return", note: "The last return under the old system for most people." },
  { date: "6 April 2026", title: "Start keeping records with MTD software", note: "Every transaction from this date needs to be recorded digitally." },
  { date: "30 June 2026", title: "Finalise your 2025/26 tax return", note: "HMRC deadline is 31 January 2027 — our target is 30 June 2026 so you're clear before quarterly updates begin." },
  { date: "7 August 2026", title: "Quarterly Update 1", note: "Covers 6 April – 5 July" },
  { date: "7 November 2026", title: "Quarterly Update 2", note: "Covers 6 July – 5 October" },
  { date: "7 February 2027", title: "Quarterly Update 3", note: "Covers 6 October – 5 January" },
  { date: "7 May 2027", title: "Quarterly Update 4", note: "Covers 6 January – 5 April" },
  { date: "31 January 2028", title: "First MTD tax return submitted", note: "The final declaration for 2026/27." },
];

const QUARTERS = [
  { q: "Quarter 1", period: "6 April – 5 July", due: "7 August" },
  { q: "Quarter 2", period: "6 July – 5 October", due: "7 November" },
  { q: "Quarter 3", period: "6 October – 5 January", due: "7 February" },
  { q: "Quarter 4", period: "6 January – 5 April", due: "7 May" },
];

const GET_READY = [
  { title: "Ensure you have compatible software", body: "Xero, QuickBooks and others all work — but your setup matters. Contact us to discuss your options and find the best fit for you." },
  { title: "Get into a quarterly reporting mindset", body: "We will submit four quarterly updates as well as your annual tax return. Keeping records up to date becomes essential — organisation is key." },
  { title: "Decide how we can best help you", body: "You may wish to do your own bookkeeping, or for us to do everything. It's important to come to an arrangement that suits you." },
  { title: "Check whether you're exempt", body: "Exemptions exist for those who are 'digitally excluded' due to age, disability, religion or location. We can help you apply." },
  { title: "Not mandated yet? Start early", body: "If your start date is 2027 or 2028, getting into digital habits now means no scramble later — and we can sign you up voluntarily." },
];

export default function MtdPage() {
  useSeo({
    title: "Making Tax Digital for Income Tax: Your Roadmap | Accountants in Bristol & Bath",
    description:
      "Who MTD for Income Tax affects, what changes, your start date and every key deadline from April 2026 — explained by Rosevale, small business accountants in Bristol & Bath.",
    path: "/making-tax-digital",
  });

  return (
    <PageShell testid="mtd-page">
      <SubpageHero
        dark
        eyebrow="Business Toolkit · Making Tax Digital"
        title="Making Tax Digital for Income Tax: your roadmap."
        lead="Is this a journey you need to take? Here's how to know if you'll be affected, what it means for you, how to work out your start date — and every deadline that follows."
        crumbs={[{ label: "Business Toolkit", to: "/#toolkit" }, { label: "Making Tax Digital" }]}
      >
        <div className="mt-8 flex flex-wrap items-center gap-4">
          <a
            href="https://calendly.com/abbie-browne"
            target="_blank"
            rel="noreferrer"
            data-testid="mtd-book-call-button"
            className="group inline-flex items-center gap-2 rounded-full bg-[#40C0E0] text-[#103080] text-sm font-semibold px-7 py-3.5 hover:bg-white transition-colors duration-300"
          >
            Book a free MTD check-in
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <GuideDownloadButton
            endpoint="guides/mtd"
            eyebrow="Making Tax Digital · Free roadmap"
            title="Your MTD roadmap PDF"
            label="Download the roadmap PDF"
            accent="#40C0E0"
            buttonClassName="group inline-flex items-center gap-2 rounded-full border border-white/25 text-white text-sm font-medium px-7 py-3.5 hover:bg-white/10 transition-colors duration-300"
          />
          <span className="inline-flex items-center gap-2 text-sm text-white/60"><AlertTriangle size={15} className="text-[#40C0E0]" /> MTD is mandatory — penalties apply for non-compliance from April 2027</span>
        </div>
      </SubpageHero>

      <section className="py-20 sm:py-28 bg-[#FDFBF7]" data-testid="mtd-how-it-works">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-[#0090D0] font-medium mb-5">How it works</p>
            <h2 className="font-serif font-semibold tracking-tight text-[#103080] text-3xl sm:text-4xl lg:text-5xl leading-tight max-w-2xl mb-12">
              Three things you'll need to do.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {[
              "Keep records of your business transactions in functional, compatible software.",
              "Each quarter, send HMRC a digital summary of the transactions in the software.",
              "At the end of each tax year, make an end-of-year submission to finalise your tax position.",
            ].map((t, i) => (
              <Reveal key={t} delay={i * 0.08}>
                <div className="rounded-3xl bg-[#103080] text-white p-7 h-full">
                  <span className="font-serif text-5xl text-[#40C0E0]">{i + 1}</span>
                  <p className="mt-4 text-white/85 leading-relaxed">{t}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {HOW_IT_WORKS.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.06}>
                <div className="h-full rounded-3xl bg-white border border-[#103080]/10 p-7 lift-shadow" data-testid={`mtd-card-${i}`}>
                  <span className="w-11 h-11 rounded-2xl bg-[#40C0E0]/15 flex items-center justify-center text-[#0090D0]"><c.icon size={20} strokeWidth={1.75} /></span>
                  <h3 className="font-serif font-semibold text-xl text-[#103080] mt-5">{c.title}</h3>
                  <ul className="mt-4 space-y-2.5">
                    {c.items.map((it) => (
                      <li key={it} className="flex gap-2.5 text-sm text-[#0F172A]/70 leading-relaxed"><CheckCircle2 size={16} className="shrink-0 mt-0.5 text-[#0090D0]" />{it}</li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28 bg-[#F5F2EB] border-y border-[#103080]/10" data-testid="mtd-start-dates">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.3em] text-[#0090D0] font-medium mb-5">Your start date</p>
            <h2 className="font-serif font-semibold tracking-tight text-[#103080] text-3xl sm:text-4xl leading-tight">When do the rules apply to you?</h2>
            <p className="mt-5 text-[#0F172A]/65 leading-relaxed">
              Your start date depends on your <strong className="text-[#103080] font-semibold">qualifying income</strong> — your total gross income from self-employment and property, before expenses — in an earlier tax year.
            </p>
            <div className="mt-6 flex gap-3 rounded-2xl bg-[#103080] text-white p-5">
              <AlertTriangle size={20} className="shrink-0 text-[#40C0E0]" />
              <p className="text-sm leading-relaxed text-white/85"><strong className="text-white">Beware!</strong> The MTD rules are mandatory and there are penalties for non-compliance from April 2027. If you're close to a threshold, don't wait to find out.</p>
            </div>
          </Reveal>
          <div className="lg:col-span-7 space-y-4">
            {START_DATES.map((s, i) => (
              <Reveal key={s.start} delay={i * 0.08}>
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8 rounded-2xl bg-white border border-[#103080]/10 p-6" data-testid={`mtd-start-${i}`}>
                  <div className="sm:w-56">
                    <p className="font-serif font-semibold text-2xl text-[#103080]">{s.income}</p>
                    <p className="text-xs text-[#0F172A]/50 mt-1">{s.year}</p>
                  </div>
                  <div className="flex-1 flex items-center justify-between gap-4">
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.2em] text-[#0F172A]/45">MTD start date</p>
                      <p className="text-lg font-medium text-[#103080] mt-0.5">{s.start}</p>
                    </div>
                    <span className={`rounded-full px-3.5 py-1.5 text-[11px] uppercase tracking-[0.18em] font-medium ${i === 0 ? "bg-[#40C0E0]/20 text-[#0090D0]" : "bg-[#F5F2EB] text-[#0F172A]/55"}`}>{s.status}</span>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28 bg-[#FDFBF7]" data-testid="mtd-key-dates">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-[#0090D0] font-medium mb-5">Key dates & deadlines</p>
            <h2 className="font-serif font-semibold tracking-tight text-[#103080] text-3xl sm:text-4xl lg:text-5xl leading-tight max-w-2xl mb-14">
              Tax years 2025/26 → 2026/27, step by step.
            </h2>
          </Reveal>
          <ol className="relative border-l-2 border-[#103080]/15 ml-3 sm:ml-5 space-y-10">
            {KEY_DATES.map((k, i) => (
              <Reveal key={k.date} delay={i * 0.04}>
                <li className="relative pl-10 sm:pl-14" data-testid={`mtd-date-${i}`}>
                  <span className="absolute -left-[21px] top-0 w-10 h-10 rounded-full bg-[#103080] text-white font-serif text-sm flex items-center justify-center ring-4 ring-[#FDFBF7]">{String(i + 1).padStart(2, "0")}</span>
                  <p className="text-xs uppercase tracking-[0.25em] text-[#0090D0] font-medium">{k.date}</p>
                  <h3 className="font-serif font-semibold text-xl sm:text-2xl text-[#103080] mt-1.5">{k.title}</h3>
                  <p className="text-sm text-[#0F172A]/60 mt-1.5 leading-relaxed max-w-xl">{k.note}</p>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      <section className="py-20 sm:py-28 bg-[#103080] text-white" data-testid="mtd-quarters">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-[#40C0E0] font-medium mb-5">Every year from now on</p>
            <h2 className="font-serif font-semibold tracking-tight text-3xl sm:text-4xl leading-tight max-w-2xl mb-12">MTD quarterly update deadlines.</h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {QUARTERS.map((q, i) => (
              <Reveal key={q.q} delay={i * 0.06}>
                <div className="rounded-3xl bg-white/[0.07] border border-white/10 p-7 h-full">
                  <p className="text-[11px] uppercase tracking-[0.25em] text-[#40C0E0]">{q.q}</p>
                  <p className="mt-3 text-white/70 text-sm">{q.period}</p>
                  <p className="mt-5 text-xs text-white/45 uppercase tracking-[0.2em]">Due by</p>
                  <p className="font-serif font-semibold text-3xl mt-1">{q.due}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28 bg-[#F5F2EB] border-y border-[#103080]/10" data-testid="mtd-get-ready">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-4">
            <p className="text-xs uppercase tracking-[0.3em] text-[#0090D0] font-medium mb-5">Getting ready</p>
            <h2 className="font-serif font-semibold tracking-tight text-[#103080] text-3xl sm:text-4xl leading-tight">A roadmap to your start date.</h2>
            <p className="mt-5 text-[#0F172A]/65 leading-relaxed">Once you know your likely start date, follow these steps and you're ready for MTD for Income Tax.</p>
          </Reveal>
          <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {GET_READY.map((g, i) => (
              <Reveal key={g.title} delay={i * 0.05}>
                <div className="h-full rounded-2xl bg-white border border-[#103080]/10 p-6">
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-full bg-[#40C0E0]/20 text-[#0090D0] text-sm font-semibold flex items-center justify-center">{i + 1}</span>
                    <h3 className="font-semibold text-[#103080]">{g.title}</h3>
                  </div>
                  <p className="mt-3 text-sm text-[#0F172A]/65 leading-relaxed">{g.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="rounded-3xl bg-white border border-[#103080]/10 p-8 sm:p-10 flex flex-col lg:flex-row lg:items-center justify-between gap-8 lift-shadow">
              <div>
                <h2 className="font-serif font-semibold text-2xl sm:text-3xl text-[#103080]">Need help getting ready for MTD?</h2>
                <p className="mt-2 text-[#0F172A]/65">We'll work out your start date, sort your software and keep every quarterly deadline for you.</p>
              </div>
              <div className="flex flex-wrap gap-3">
                <a href="mailto:info@rosevaleaccountants.co.uk" className="inline-flex items-center gap-2 rounded-full border border-[#103080]/15 px-5 py-3 text-sm font-medium text-[#103080] hover:border-[#0090D0] hover:text-[#0090D0] transition-colors duration-300"><Mail size={15} /> info@rosevaleaccountants.co.uk</a>
                <a href="tel:01173210360" className="inline-flex items-center gap-2 rounded-full border border-[#103080]/15 px-5 py-3 text-sm font-medium text-[#103080] hover:border-[#0090D0] hover:text-[#0090D0] transition-colors duration-300"><Phone size={15} /> 0117 321 0360</a>
                <a href="https://calendly.com/abbie-browne" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#103080] text-white px-6 py-3 text-sm font-medium hover:bg-[#0090D0] transition-colors duration-300">Book a call <ArrowUpRight size={15} /></a>
                <GuideDownloadButton
                  endpoint="guides/mtd"
                  index={1}
                  eyebrow="Making Tax Digital · Free roadmap"
                  title="Your MTD roadmap PDF"
                  label="Download the roadmap PDF"
                  buttonClassName="inline-flex items-center gap-2 rounded-full border border-[#103080]/15 px-5 py-3 text-sm font-medium text-[#103080] hover:border-[#0090D0] hover:text-[#0090D0] transition-colors duration-300"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </PageShell>
  );
}
